import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useCreatePlan } from "@/api/plan/add-new-plan";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Validation schema
const packageSchema = z.object({
    name: z.string().min(1, "Package name is required"),
    price: z
        .string()
        .nullable()
        .refine((val) => val === null || (!isNaN(Number(val)) && Number(val) > -1), {
            message: "Price must be a positive number or null",
        }),
    features: z.string().nullable().optional(),
    duration: z.string().min(1, "Duration is required"),
    isActive: z.boolean(),
});

type PackageFormData = z.infer<typeof packageSchema>;

export default function CreatePackageDialog() {
    const [open, setOpen] = useState(false);
    
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm<PackageFormData>({
        resolver: zodResolver(packageSchema),
        defaultValues: {
            name: "",
            price: "",
            features: "",
            duration: "month",
            isActive: true,
        },
    });

    const { mutate: createPlan, isPending } = useCreatePlan();

    const onSubmit = (data: PackageFormData) => {
        createPlan(
            {
                name: data.name,
                price: data.price ?? undefined,
                description: data.features ?? undefined,
                plans_interval: data.duration,
                is_active: data.isActive,
            },
            {
                onSuccess: () => {
                    reset();
                    setOpen(false);
                },
            }
        );
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">+ New Package</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create Package</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Package Name</Label>
                        <Input id="name" {...register("name")} placeholder="Enter package name" />
                        {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="price">Price (USD)</Label>
                        <Input id="price" type="text" {...register("price")} placeholder="Enter price" />
                        {errors.price && <p className="text-sm text-red-500">{errors.price.message}</p>}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="duration">Duration</Label>
                        <Select value={watch("duration")} onValueChange={(value) => setValue("duration", value)}>
                            <SelectTrigger id="duration">
                                <SelectValue placeholder="Select duration" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="day">Day</SelectItem>
                                <SelectItem value="week">Week</SelectItem>
                                <SelectItem value="month">Month</SelectItem>
                                <SelectItem value="year">Year</SelectItem>
                            </SelectContent>
                        </Select>
                        {errors.duration && <p className="text-sm text-red-500">{errors.duration.message}</p>}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="features">Description</Label>
                        <Textarea id="features" {...register("features")} placeholder="List features separated by commas" />
                        {errors.features && <p className="text-sm text-red-500">{errors.features.message}</p>}
                    </div>

                    <div className="flex items-center justify-between">
                        <Label htmlFor="active">Active</Label>
                        <Switch id="active" checked={watch("isActive")} onCheckedChange={(checked) => setValue("isActive", checked)} />
                    </div>

                    <DialogFooter>
                        <Button type="submit" className="mt-2" disabled={isPending}>
                            {isPending ? "Saving..." : "Save Package"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
